import { withParams, type BuyParams } from "@indietabletop/appkit";

function withHash(url: string, fragment?: string) {
  if (!fragment) {
    return url;
  }

  return `${url}#${fragment}`;
}

function withOrigin(path: string, includeOrigin?: boolean) {
  if (includeOrigin) {
    // Use a replacer function so a literal "$" in window.origin can't be
    // misinterpreted as a String.replace special pattern (e.g. "$&", "$$").
    return path.replace(/^~/, () => window.origin);
  }

  return path;
}

type ParamNames<Path extends string> =
  Path extends `${infer Start}/${infer Rest}`
    ? Start extends `:${infer Param}`
      ? Param | ParamNames<Rest>
      : ParamNames<Rest>
    : Path extends `:${infer Param}`
      ? Param
      : never;

type RouteParams<Path extends string> = {
  [K in ParamNames<Path>]: string;
};

type HrefArgs<Params extends Record<string, string>> = {
  search?: Params;
  fragment?: string;
  withOrigin?: boolean;
};

type ParamsArgs<Path extends string, Params extends Record<string, string>> =
  ParamNames<Path> extends never
    ? [params?: HrefArgs<Params>]
    : [params: { path: RouteParams<Path> } & HrefArgs<Params>];

type WithSearchParams<PathDefinition> = PathDefinition extends {
  search: infer SearchParams extends Record<string, string>;
}
  ? SearchParams
  : never;

function replacePathParams(path: string, params: Record<string, string>) {
  return path.replace(/:([A-Za-z0-9_]+)/g, (_match, key: string) => {
    if (!(key in params)) {
      throw new Error(`Missing route param "${key}" for path "${path}"`);
    }

    return encodeURIComponent(params[key]);
  });
}

/**
 * Creates a `href()` util that accepts parameters based on the provided Paths
 * spec, and intelligently decides what params are necessary.
 *
 * @example
 * ```ts
 * type Paths = {
 *   "~/foo/:bar": { // Path param will be `bar: string`
 *     search: { beep: string }; // Search params will be `beep: string`
 *   };
 *
 *   "~/": never; // No search path/search params
 * };
 *
 * const href = createHref<Paths>()
 *
 * href("~/") // Type-checks!
 * href("~/foo/:bar", { path: { bar: "zxc"}}) // Type-checks!
 * ```
 */
function createHref<
  Paths extends Record<string, { search?: Record<string, string> }>,
>() {
  return function <Path extends keyof Paths & string>(
    url: Path,
    ...args: ParamsArgs<Path, WithSearchParams<Paths[Path]>>
  ) {
    const [params] = args;

    const base =
      !params || !("path" in params)
        ? url
        : replacePathParams(url, params.path);

    return withOrigin(
      withHash(withParams(base, params?.search), params?.fragment),
      params?.withOrigin,
    );
  };
}

type Paths = {
  /**
   * Dashboard page.
   */
  "~/": never;

  /**
   * The Gravehelm landing page.
   */
  "~/gravehelm": never;

  /**
   * Docs page. Includes both changelog and help pages.
   */
  "~/docs": {
    search: NavParams;
  };

  /**
   * Login page.
   */
  "~/login": {
    search: NavParams;
  };

  "~/join": {
    search: NavParams;
  };

  /**
   * Change email page.
   */
  "~/change-email": {
    search: NavParams;
  };

  /**
   * Shop page.
   *
   * This is where the user can purchase Greathelm content.
   *
   * Previously: `~/rules`, `~/buy`.
   */
  "~/shop": {
    search: NavParams & BuyParams;
  };

  /**
   * Account settings page.
   */
  "~/settings": {
    search: NavParams;
  };

  /**
   * Root page for the list builder.
   */
  "~/builder": {
    search: NavParams;
  };

  /**
   * Warband detail page.
   */
  "~/builder/w/:warbandId": {
    search: NavParams;
  };

  /**
   * Knight detail page.
   */
  "~/builder/w/:warbandId/k/:knightId": {
    search: NavParams;
  };

  /**
   * Warband print page.
   */
  "~/builder/w/:warbandId/print": {
    search: NavParams;
  };

  /**
   * Shared warband detail page.
   */
  "~/shared/w/:warbandId": {
    search: NavParams;
  };
};

type NavParams = {
  backTo?: string;
  redirectTo?: string;
};

export const href = createHref<Paths>();
